import { api } from '@/lib/api'
import type {
  ChatbotRequest,
  ChatbotResponse,
  ChatMessage,
  ChatThread,
  ChatThreadSummary,
  ChatThreadVisibility,
} from './types'

export const chatbotApi = {
  send: (payload: ChatbotRequest) =>
    api.post<ChatbotResponse>('/api/chatbot', payload),

  listThreads: () =>
    api.get<{ threads: ChatThreadSummary[] }>('/api/chatbot/threads'),

  createThread: (payload: ChatCreateThreadPayload = {}) =>
    api.post<{ thread: ChatThread }>('/api/chatbot/threads', payload),

  getThreadMessages: (id: string) =>
    api.get<{ thread: ChatThread; messages: ChatMessage[] }>(
      `/api/chatbot/threads/${id}/messages`,
    ),

  renameThread: (id: string, title: string) =>
    api.patch<{ thread: ChatThread }>(`/api/chatbot/threads/${id}`, { title }),

  setThreadVisibility: (
    id: string,
    payload: {
      visibility: ChatThreadVisibility
      shared_user_ids?: number[]
      shared_role_ids?: number[]
    },
  ) =>
    api.patch<{ thread: ChatThread }>(
      `/api/chatbot/threads/${id}/visibility`,
      payload,
    ),

  getThreadVisibility: (id: string) =>
    api.get<{
      visibility: ChatThreadVisibility
      shared_users: Array<{ user_id: number; email: string | null; name: string | null }>
      shared_roles: Array<{ role_id: number; name: string }>
    }>(`/api/chatbot/threads/${id}/visibility`),

  deleteThread: (id: string) => api.del<void>(`/api/chatbot/threads/${id}`),

  deleteAllThreads: () =>
    api.del<{ deleted: number }>('/api/chatbot/threads'),

  sendFeedback: (
    messageId: string,
    payload: { rating: 1 | -1 | 0; comment?: string },
  ) =>
    api.post<{ feedback: { id: string | null; rating: number; comment: string | null } }>(
      `/api/chatbot/messages/${messageId}/feedback`,
      payload,
    ),

  listThreadFeedback: (threadId: string) =>
    api.get<{
      feedback: Array<{
        message_id: string
        user_id: number | null
        rating: number
        is_mine: boolean
        comment: string | null
        created_at: string
      }>
    }>(`/api/chatbot/threads/${threadId}/feedback`),

  listShares: (threadId: string) =>
    api.get<{ shares: ShareLinkSummary[] }>(`/api/chatbot/threads/${threadId}/shares`),

  createShare: (threadId: string, payload: { expires_at?: string } = {}) =>
    api.post<{ share: ShareLinkSummary }>(`/api/chatbot/threads/${threadId}/shares`, payload),

  revokeShare: (shareId: string) => api.del<void>(`/api/chatbot/shares/${shareId}`),
}

export interface ShareLinkSummary {
  id: string
  created_at: string
  revoked_at: string | null
  expires_at: string | null
  url: string | null
}

// Payload accepted by the create-thread route. visibility defaults to
// 'private' on the backend; pass 'tenant' for "share with workspace" or
// 'shared' with explicit user/role lists for "share with specific roles
// or members".
export interface ChatCreateThreadPayload {
  title?: string
  agent_id?: string
  visibility?: ChatThreadVisibility
  shared_user_ids?: number[]
  shared_role_ids?: number[]
}
